import React, { useContext } from 'react';
import { createBrowserRouter, Outlet, useLocation, Navigate } from 'react-router-dom';
import { RouterProvider } from 'react-router-dom';

// route components
import { Login } from './pages/auth/login';
import { PageLayoutPage } from './layouts/page-layout.page';

// admin pages
import { NewOverview } from './pages/dashboard/admin-dashboard/newOverview/newOverview';
import { Ushers } from './pages/dashboard/admin-dashboard/usher/views/ushers.page';
import { Bets } from './pages/dashboard/admin-dashboard/dashboard-by-gametype/bets/bets.page';
import { GameResults } from './pages/dashboard/admin-dashboard/dashboard-by-gametype/game-results/game-results.page';
import { Gamelimits } from './pages/dashboard/admin-dashboard/dashboard-by-gametype/game-limits/game-limits.page';
import { OpStats } from './pages/dashboard/admin-dashboard/dashboard-by-gametype/operation-stats/opstats.page';
import { Reports } from './pages/dashboard/admin-dashboard/dashboard-by-gametype/reports/report.page';
import { ServerConfig } from './pages/dashboard/admin-dashboard/dashboard-by-gametype/server-config/server-config.page';
import { GamesPage } from './pages/dashboard/admin-dashboard/games/views/games.page';
import { Development } from './pages/dashboard/admin-dashboard/development/development.page';
import { CoordinatorsPage } from './pages/dashboard/admin-dashboard/coordinators/coordinators.page';
import { CoordinatorsReportPage } from './pages/dashboard/admin-dashboard/dashboard-by-gametype/coordinators-report/coordinators-report.page';
import { DrawAdminsPage } from './pages/dashboard/admin-dashboard/draw-admin/draw-admin.page';
import { CoordinatorPage } from './pages/dashboard/admin-dashboard/coordinator/coordinator.page';
import { NotificationsPage } from './pages/dashboard/admin-dashboard/notifications/notification.page';
import { TelegramReportsPage } from './pages/dashboard/admin-dashboard/telegram-report/telegram-report.page';
import { CashiersPage } from './pages/dashboard/admin-dashboard/cashiers/cashiers.page';
import { AuditorsPage } from './pages/dashboard/admin-dashboard/auditor/auditor.page';
import { Limits } from '@/pages/dashboard/admin-dashboard/dashboard-by-gametype/limits/limits.page';
import { CoordinatorUshersPage } from './pages/dashboard/admin-dashboard/coordinator-ushers/views/coordinator-ushers.page';
import { UsherBets } from './pages/dashboard/admin-dashboard/dashboard-by-gametype/usher-bets/usher-bets.page';
import { PlayersPage } from './pages/dashboard/admin-dashboard/players/players.page';
import { AuditorPlayerTransactionsPage } from './pages/dashboard/auditor-dashboard/transaction-history/transaction-history.page';

// cashier pages
import { PlayerAdditionWithdrawalsPage } from './pages/dashboard/cashier-dashboard/player-additions-withdrawal/player-addition-withdrawals.page';
import { AdminCashTransactionPage } from './pages/dashboard/cashier-dashboard/admin-cash-transactions/admin-cash-transactions.page';
import { ProfitLossTransactionPage } from './pages/dashboard/cashier-dashboard/profit-loss-transactions/profit-loss-transactions.page';
import { PlayerTransactionsPage } from './pages/dashboard/cashier-dashboard/player-transactions/player-transactions.page';

// auditors pages
import { AuditorDBPendingTransactionsPage } from './pages/dashboard/auditor-dashboard/pending-transactions/pending-transactions.page';

// error page
import { NotFound } from '@/pages/error/4xx/notfound';

import { AuthProvider, AuthContext } from '@/contexts/auth';

interface Props {
  setAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
}

const PrivateRoutes: React.FC = () => {
  const { authenticated } = useContext(AuthContext);

  const location = useLocation();

  if (!authenticated) {
    console.error('UnAuthenticated');
    // we have to "remember" the location from where the redirect happened to the Login page.
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Outlet />;
};

export const Routes: React.FC<Props> = ({ setAuthenticated }) => {
  const router = createBrowserRouter([
    {
      element: <AuthProvider />,
      errorElement: <NotFound />,
      children: [
        {
          path: '/',
          element: <Login setAuthenticated={setAuthenticated} />,
        },
        {
          element: <PrivateRoutes />,
          children: [
            {
              path: '/dashboard',
              element: <PageLayoutPage />,
              children: [
                {
                  path: 'overview',
                  element: <NewOverview />,
                },
                {
                  path: 'usher',
                  element: <Ushers />,
                },
                {
                  path: 'cash-and-chips',
                  element: <CashiersPage />,
                },
                {
                  path: 'usher-bets/:usherId/:usherName',
                  element: <UsherBets />,
                },
                {
                  path: 'coordinators',
                  element: <CoordinatorsPage />,
                },
                {
                  path: 'coordinator-ushers/:coordinatorId',
                  element: <CoordinatorUshersPage />,
                },
                {
                  path: 'draw-admins',
                  element: <DrawAdminsPage />,
                },
                {
                  path: 'bets',
                  element: <Bets />,
                },
                {
                  path: 'game-results',
                  element: <GameResults />,
                },
                {
                  path: 'game-limits',
                  element: <Gamelimits />,
                },
                {
                  path: 'operation-stats',
                  element: <OpStats />,
                },
                {
                  path: 'reports',
                  element: <Reports />,
                },
                {
                  path: 'server-config',
                  element: <ServerConfig />,
                },
                {
                  path: 'game-types',
                  element: <GamesPage />,
                },
                {
                  path: 'developing',
                  element: <Limits />,
                },
                {
                  path: 'development',
                  element: <Development />,
                },
                {
                  path: 'operators-report',
                  element: <CoordinatorsReportPage />,
                },
                {
                  path: 'coordinator/:coordinatorId',
                  element: <CoordinatorPage />,
                },
                {
                  path: 'notifications',
                  element: <NotificationsPage />,
                },
                {
                  path: 'telegram-report',
                  element: <TelegramReportsPage />,
                },
                {
                  path: 'auditors',
                  element: <AuditorsPage />,
                },
                {
                  path: 'players',
                  element: <PlayersPage />,
                },
              ],
            },
            {
              path: '/cashier-dashboard',
              element: <PageLayoutPage />,
              children: [
                {
                  path: 'pending-transactions',
                  element: <PlayerAdditionWithdrawalsPage />,
                },

                {
                  path: 'admin-cash-transactions',
                  element: <AdminCashTransactionPage />,
                },

                {
                  path: 'profit-loss-transactions',
                  element: <ProfitLossTransactionPage />,
                },
                {
                  path: 'player-transactions',
                  element: <PlayerTransactionsPage />,
                },
              ],
            },
            {
              path: '/auditor-dashboard',
              element: <PageLayoutPage />,
              children: [
                {
                  path: 'pending-transactions',
                  element: <AuditorDBPendingTransactionsPage />,
                },
                {
                  path: 'transactions-history',
                  element: <AuditorPlayerTransactionsPage />,
                },
              ],
            },
          ],
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
};
